import { lazy, Suspense, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Tagged } from '~/apps/smartsend/components/Tagged';
import { useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { ContentContext, useAnalytics } from '@parcel2go/npm-p2g-app';
import { RouteAppWrapper } from '~/components/route-app-wrapper';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { AppSurface } from '~/components/surface/app-surface';
import { SearchObject } from '~/components/search/algolia/Models';
import useGoto from '~/hooks/use-goto';
import { GenerateEnquryLink } from '~/apps/smartsend/lib/go-helpers';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '../use-on-load';

const SelectOrder = lazy(() => import('~/apps/self-serve/pages/OpenEnquiry/stages/SelectOrder'));

export default function Component() {
  const isXs = useP2GMediaQueryOnly('xs');
  const { t } = useTranslation(TranslationNamespaces.SELFSERVE);
  const { goTo } = useGoto();
  const { track } = useAnalytics();
  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);

  useOnLoad(() => {
    const title = t('OPEN_ENQUIRY_PAGE_TITLE', { defaultValue: 'How Can We Help?' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.SELFSERVE });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: { title: title, currentPageRoute: RoutingConstants.Enquiry }
    });
    track({
      type: 'page_view',
      payload: {
        ...GenerateBaseAnalyticsPayload([]),
        page: RoutingConstants.Enquiry
      }
    });
  });

  const onOrderSelected = useCallback(
    (orderLine: SearchObject | null) => {
      goTo(GenerateEnquryLink(orderLine.orderLineId));
    },
    [goTo]
  );

  return (
    <Tagged tag="screen-open-enquiry">
      <RouteAppWrapper isXs={isXs}>
        <AppSurface>
          <Suspense>
            <SelectOrder currentStep={1} totalSteps={3} isXs={isXs} onOrderSelected={onOrderSelected} />
          </Suspense>
        </AppSurface>
      </RouteAppWrapper>
    </Tagged>
  );
}

Component.displayName = 'Enquiry';
